import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../services/api';
import { Search, AlertCircle, Loader, X, Plus, ChevronDown, AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import CreateComplaintModal from '../components/CreateComplaintModal';

const AdminComplaints = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ priority: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [showClosedComplaints, setShowClosedComplaints] = useState(false);
  const highlightRef = useRef(null);

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      let allComplaints = [];
      let nextPage = '/complaints/';
      while (nextPage) {
        const res = await api.get(nextPage);
        allComplaints = [...allComplaints, ...(res.data.results || [])];
        nextPage = res.data.next ? res.data.next.replace(api.defaults.baseURL, '') : null;
      }
      setComplaints(allComplaints);
    } catch (err) {
      setError('Failed to load complaints');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchComplaints(); }, []);

  // Check for highlight param from notification
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (highlight) {
      setHighlightId(parseInt(highlight));
      // Clean URL
      searchParams.delete('highlight');
      setSearchParams(searchParams, { replace: true });
      // Clear highlight after 4 seconds
      setTimeout(() => setHighlightId(null), 4000);
    }
  }, [searchParams]);

  // Scroll to highlighted complaint
  useEffect(() => {
    if (highlightId && !loading && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, loading]);

  const handleClose = async (id) => {
    setStatusUpdating(id);
    try {
      await api.post(`/complaints/${id}/close/`);
      await fetchComplaints();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close complaint');
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setStatusUpdating(id);
    try {
      await api.patch(`/complaints/${id}/update-status/`, { status: newStatus });
      await fetchComplaints();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusUpdating(null);
    }
  };

  const filteredComplaints = useMemo(() => {
    return complaints.filter(c => {
      const matchesSearch = !searchTerm ||
        c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.customer_username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.customer_phone?.includes(searchTerm);
      const matchesPriority = !filters.priority || c.priority === filters.priority;
      const matchesStatus = !filters.status || c.status === filters.status;
      return matchesSearch && matchesPriority && matchesStatus;
    });
  }, [complaints, searchTerm, filters]);

  const activeComplaints = useMemo(() => {
    return filteredComplaints.filter(c => c.status === 'open' || c.status === 'in_progress');
  }, [filteredComplaints]);

  const closedComplaints = useMemo(() => {
    return filteredComplaints.filter(c => c.status === 'resolved' || c.status === 'closed');
  }, [filteredComplaints]);

  const priorityConfig = {
    low: { color: 'bg-gray-100 text-gray-700', label: 'Low' },
    medium: { color: 'bg-blue-100 text-blue-700', label: 'Medium' },
    high: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', label: 'High' },
    critical: { color: 'bg-red-100 text-red-700', label: 'Critical' },
  };

  const statusConfig = {
    open: { color: 'bg-blue-100 text-blue-700', icon: AlertCircle, label: 'Open' },
    in_progress: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'In Progress' },
    resolved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Resolved' },
    closed: { color: 'bg-gray-100 text-gray-700', icon: XCircle, label: 'Closed' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-500" size={18} />
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X size={16} className="text-red-500" /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Complaints</h1>
          <p className="text-sm text-gray-500">{activeComplaints.length} active of {filteredComplaints.length} filtered, {complaints.length} total</p>
        </div>
        <button onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-md shadow-blue-200 transition-all">
          <Plus size={18} /> New Complaint
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search complaints..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <select value={filters.priority} onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Active Complaints Section - Desktop Table */}
      <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 px-4 py-3 border-b border-blue-200 dark:border-blue-800">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <Clock size={18} className="text-blue-600" />
            Active Complaints ({activeComplaints.length})
          </h2>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Technician</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {activeComplaints.map(c => {
              const priority = priorityConfig[c.priority] || priorityConfig.medium;
              const status = statusConfig[c.status] || statusConfig.open;
              const StatusIcon = status.icon;
              return (
                <tr key={c.id} ref={c.id === highlightId ? highlightRef : undefined} className={`hover:bg-gray-50 transition-all ${c.id === highlightId ? 'bg-blue-50 ring-2 ring-blue-400' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{c.customer_username}</div>
                    <div className="text-xs text-gray-500">{c.customer_name}</div>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    {c.complaint_number && (
                      <div className="text-[10px] font-mono text-blue-600 mb-0.5">{c.complaint_number}</div>
                    )}
                    <div className="text-sm text-gray-900 truncate">{c.title}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${priority.color}`}>{priority.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${status.color}`}>
                      <StatusIcon size={12} /> {status.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{c.technician_name || <span className="text-gray-400">Unassigned</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{c.complaint_date}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {c.status === 'resolved' ? (
                        <button
                          onClick={() => handleClose(c.id)}
                          disabled={statusUpdating === c.id}
                          className="px-3 py-1.5 bg-gray-600 text-white text-xs rounded-lg hover:bg-gray-700 flex items-center gap-1 transition-colors disabled:opacity-50"
                        >
                          <XCircle size={12} /> Close
                        </button>
                      ) : c.status !== 'closed' ? (
                        <select
                          value={c.status}
                          onChange={(e) => handleStatusUpdate(c.id, e.target.value)}
                          disabled={statusUpdating === c.id}
                          className="text-xs px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                        </select>
                      ) : (
                        <span className="text-xs text-gray-400">Closed</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {activeComplaints.length === 0 && (
          <div className="text-center py-12">
            <CheckCircle className="mx-auto text-green-300" size={48} />
            <p className="mt-2 text-gray-500">No active complaints</p>
          </div>
        )}
      </div>

      {/* Closed Complaints Section - Collapsible */}
      {closedComplaints.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowClosedComplaints(!showClosedComplaints)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-gray-100"
          >
            <div className="flex items-center gap-2">
              <ChevronDown size={18} className={`text-gray-600 transition-transform ${showClosedComplaints ? 'rotate-180' : ''}`} />
              <h2 className="font-semibold text-gray-800">Closed & Resolved Complaints ({closedComplaints.length})</h2>
            </div>
          </button>
          {showClosedComplaints && (
            <div className="hidden lg:block overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Technician</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-gray-50">
                  {closedComplaints.map(c => {
                    const priority = priorityConfig[c.priority] || priorityConfig.medium;
                    const status = statusConfig[c.status] || statusConfig.open;
                    const StatusIcon = status.icon;
                    return (
                      <tr key={c.id} ref={c.id === highlightId ? highlightRef : undefined} className={`hover:bg-gray-100 transition-all ${c.id === highlightId ? 'bg-blue-50 ring-2 ring-blue-400' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{c.customer_username}</div>
                          <div className="text-xs text-gray-500">{c.customer_name}</div>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                    {c.complaint_number && (
                      <div className="text-[10px] font-mono text-blue-600 mb-0.5">{c.complaint_number}</div>
                    )}
                    <div className="text-sm text-gray-900 truncate">{c.title}</div>
                  </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${priority.color}`}>{priority.label}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${status.color}`}>
                            <StatusIcon size={12} /> {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c.technician_name || <span className="text-gray-400">Unassigned</span>}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{c.complaint_date}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">Closed</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {/* Active Complaints */}
        {activeComplaints.length > 0 && (
          <div>
            <h2 className="font-semibold text-gray-800 px-1 mb-3 flex items-center gap-2">
              <Clock size={16} className="text-blue-600" />
              Active Complaints ({activeComplaints.length})
            </h2>
            <div className="space-y-3">
              {activeComplaints.map(c => {
                const priority = priorityConfig[c.priority] || priorityConfig.medium;
                const status = statusConfig[c.status] || statusConfig.open;
                const StatusIcon = status.icon;
                return (
                  <div key={c.id} ref={c.id === highlightId ? highlightRef : undefined} className={`bg-white rounded-xl border p-4 transition-all ${c.id === highlightId ? 'border-blue-400 ring-2 ring-blue-200 bg-blue-50' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        {c.complaint_number && (
                          <p className="text-[10px] font-mono text-blue-600 mb-0.5">{c.complaint_number}</p>
                        )}
                        <h3 className="font-semibold text-gray-900">{c.title}</h3>
                        <p className="text-xs text-gray-500">{c.customer_username} &middot; {c.complaint_date}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${priority.color}`}>{priority.label}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                        <StatusIcon size={12} /> {status.label}
                      </span>
                      <span className="text-xs text-gray-500">{c.technician_name || 'Unassigned'}</span>
                    </div>
                    {c.status === 'resolved' ? (
                      <button
                        onClick={() => handleClose(c.id)}
                        disabled={statusUpdating === c.id}
                        className="mt-3 w-full px-3 py-1.5 bg-gray-600 text-white text-xs rounded-lg hover:bg-gray-700 flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                      >
                        <XCircle size={12} /> Close Complaint
                      </button>
                    ) : c.status !== 'closed' ? (
                      <select
                        value={c.status}
                        onChange={(e) => handleStatusUpdate(c.id, e.target.value)}
                        disabled={statusUpdating === c.id}
                        className="mt-3 w-full text-xs px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                      </select>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Closed Complaints Mobile */}
        {closedComplaints.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowClosedComplaints(!showClosedComplaints)}
              className="w-full flex items-center justify-between px-3 py-3 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors mb-3"
            >
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                <ChevronDown size={16} className={`text-gray-600 transition-transform ${showClosedComplaints ? 'rotate-180' : ''}`} />
                Closed & Resolved ({closedComplaints.length})
              </h2>
            </button>
            {showClosedComplaints && (
              <div className="space-y-3">
                {closedComplaints.map(c => {
                  const priority = priorityConfig[c.priority] || priorityConfig.medium;
                  const status = statusConfig[c.status] || statusConfig.open;
                  const StatusIcon = status.icon;
                  return (
                    <div key={c.id} ref={c.id === highlightId ? highlightRef : undefined} className={`bg-white rounded-xl border p-4 transition-all ${c.id === highlightId ? 'border-blue-400 ring-2 ring-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          {c.complaint_number && (
                            <p className="text-[10px] font-mono text-blue-600 mb-0.5">{c.complaint_number}</p>
                          )}
                          <h3 className="font-semibold text-gray-900">{c.title}</h3>
                          <p className="text-xs text-gray-500">{c.customer_username} &middot; {c.complaint_date}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${priority.color}`}>{priority.label}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                          <StatusIcon size={12} /> {status.label}
                        </span>
                        <span className="text-xs text-gray-500">{c.technician_name || 'Unassigned'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateComplaintModal
          onClose={() => setShowCreateModal(false)}
          onSave={fetchComplaints}
        />
      )}
    </div>
  );
};

export default AdminComplaints;
